import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWallet, type NetworkOption } from '@/hooks/useWallet';
import { useAppNetwork } from '@/hooks/useAppNetwork';

const NETWORKS: { value: NetworkOption; label: string }[] = [
  { value: 'mainnet', label: 'Mainnet' },
  { value: 'ghostnet', label: 'Ghostnet' },
  { value: 'sandbox', label: 'Localhost' },
];

export function WalletButton() {
  const [copied, setCopied] = useState(false);
  const [showNetworkMenu, setShowNetworkMenu] = useState(false);
  const { network: appNetwork, setNetwork: setAppNetwork } = useAppNetwork();
  const { isConnected, address, balance, network, disconnect, switchNetwork, isLoading } = useWallet();

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatBalance = (bal: string) => {
    const tez = parseInt(bal) / 1000000;
    return `${tez.toFixed(2)} ꜩ`;
  };

  const getNetworkDisplay = (net: string) => {
    switch (net) {
      case 'mainnet':
        return { name: 'mainnet', color: 'bg-green-500' };
      case 'ghostnet':
        return { name: 'ghostnet', color: 'bg-yellow-500' };
      case 'custom':
      case 'sandbox':
        return { name: 'localhost', color: 'bg-blue-500' };
      default:
        return { name: net, color: 'bg-gray-500' };
    }
  };

  const copyAddress = async () => {
    if (!address) return;
    await navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isConnected && address) {
    const networkInfo = network ? getNetworkDisplay(network) : null;
    return (
      <div className="flex items-center gap-4">
        <div className="text-sm">
          <div className="flex items-center gap-1">
            <Link
              to="/my-nfts"
              className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
            >
              {formatAddress(address)}
            </Link>
            <button
              onClick={copyAddress}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              title={copied ? 'Copied!' : 'Copy address'}
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            {balance && <span>{formatBalance(balance)}</span>}
            {networkInfo && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${networkInfo.color}`}></span>
                  {networkInfo.name}
                </span>
              </>
            )}
          </div>
        </div>
                <button
          onClick={disconnect}
          disabled={isLoading}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          Disconnect
        </button>
      </div>
    );
  }

  const selectedNetworkInfo = getNetworkDisplay(appNetwork);

  return (
    <div className="flex items-center gap-2">
      {import.meta.env.DEV && (
        <div className="relative">
          <button
            onClick={() => setShowNetworkMenu(!showNetworkMenu)}
            disabled={isLoading}
            className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            <span className={`w-2 h-2 rounded-full ${selectedNetworkInfo.color}`}></span>
            {selectedNetworkInfo.name}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showNetworkMenu && (
            <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
              {NETWORKS.map((net) => (
                <button
                  key={net.value}
                  onClick={() => {
                    setAppNetwork(net.value);
                    setShowNetworkMenu(false);
                    // If already connected, switch the wallet network too
                    if (isConnected) {
                      switchNetwork(net.value);
                    }
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg ${
                    appNetwork === net.value
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {net.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => switchNetwork(appNetwork)}
        disabled={isLoading}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {isLoading ? 'Connecting...' : 'Connect Wallet'}
      </button>
    </div>
  );
}
